/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUniversity,
  faGraduationCap,
  faBriefcase,
  faEnvelope,
  faGlobe,
  faUsers,
  faArrowLeft,
  faPhone,
  faMapMarkerAlt,
  faEdit,
  faSave,
  faInfoCircle,
  faCamera,
  faImage
} from '@fortawesome/free-solid-svg-icons';
import {
  faLinkedin,
  faFacebook,
  faTwitter
} from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';

// NOTE: This component copies the layout and design patterns from EmployerMyProfile.jsx,
// but uses university-specific content and a lighter data model.

const KENYAN_COUNTIES = [
  "Baringo", "Bomet", "Bungoma", "Busia", "Elgeyo-Marakwet", "Embu", "Garissa", "Homa Bay",
  "Isiolo", "Kajiado", "Kakamega", "Kericho", "Kiambu", "Kilifi", "Kirinyaga", "Kisii",
  "Kisumu", "Kitui", "Kwale", "Laikipia", "Lamu", "Machakos", "Makueni", "Mandera",
  "Marsabit", "Meru", "Migori", "Mombasa", "Murang'a", "Nairobi", "Nakuru", "Nandi",
  "Narok", "Nyamira", "Nyandarua", "Nyeri", "Samburu", "Siaya", "Taita-Taveta", "Tana River",
  "Tharaka-Nithi", "Trans Nzoia", "Turkana", "Uasin Gishu", "Vihiga", "Wajir", "West Pokot"
];

const INSTITUTION_TYPES = [
  "Public University",
  "Private University",
  "Technical Institute",
  "College",
  "Polytechnic",
  "Teacher Training College",
  "Medical Training College"
];

const UniversityMyProfile = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [editingSections, setEditingSections] = useState({
    institution: false,
    contact: false,
    details: false,
    career: false,
    specialization: false,
    social: false
  });

  const [saving, setSaving] = useState(false);
  const [profileCompletionPercentage, setProfileCompletionPercentage] = useState(0);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const profilePhotoInputRef = useRef(null);
  const [institutionLogoFile, setInstitutionLogoFile] = useState(null);
  const [institutionLogoPreview, setInstitutionLogoPreview] = useState(null);
  const institutionLogoInputRef = useRef(null);

  const [profileForm, setProfileForm] = useState({
    // Institution Information (from registration form)
    universityName: '',
    institutionType: '',
    yearEstablished: '',
    registrationNumber: '',
    cueRegistrationNumber: '',
    institutionLogo: '',
    showLogoOnWebsite: true,

    // Contact Information (from registration form)
    officialEmail: '',
    alternativeEmail: '',
    phoneNumber: '',
    whatsappNumber: '',
    physicalAddress: '',
    city: '',
    county: '',
    postalCode: '',
    latitude: '',
    longitude: '',

    // Primary Contact Person (Placement Officer)
    contactPersonName: '',
    contactPersonTitle: '',
    contactPersonDepartment: '',
    contactPersonPhone: '',
    contactPersonEmail: '',

    // Institution Details
    websiteUrl: '',
    totalStudentEnrollment: '',
    numberOfPrograms: '',
    accreditationBodies: '',
    institutionDescription: '',

    // Career Services Information
    careerCenterEmail: '',
    careerOfficePhone: '',
    careerServicesContactPerson: '',

    // Areas of Specialization (stored as free text summary here)
    areasOfSpecialization: '',

    // Social Media & Additional Information
    linkedinProfile: '',
    facebookPage: '',
    twitterHandle: '',
    languagePreference: 'English',

    // Branding / visibility
    showLogoOnWebsite: true,
    
    // Profile Photo
    profilePhotoPath: ''
  });

  // Compute primary location for display
  const primaryLocation = useMemo(() => {
    const parts = [];
    if (profileForm.city) parts.push(profileForm.city);
    if (profileForm.county) parts.push(profileForm.county);
    return parts.length > 0 ? parts.join(', ') : null;
  }, [profileForm.city, profileForm.county]);

  // Load university profile from backend
  useEffect(() => {
    const loadUniversityProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await axios.get(
          buildApiUrl('/api/user/profile'),
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data) {
          const data = response.data;

          setProfileForm(prev => ({
            ...prev,
            universityName: data.universityName || prev.universityName,
            institutionType: data.institutionType || prev.institutionType,
            yearEstablished: data.yearEstablished || prev.yearEstablished,
            registrationNumber: data.registrationNumber || prev.registrationNumber,
            cueRegistrationNumber: data.cueRegistrationNumber || prev.cueRegistrationNumber,

            officialEmail: data.officialEmail || data.email || prev.officialEmail,
            alternativeEmail: data.alternativeEmail || prev.alternativeEmail,
            phoneNumber: data.phoneNumber || data.phone || prev.phoneNumber,
            whatsappNumber: data.whatsappNumber || prev.whatsappNumber,
            physicalAddress: data.physicalAddress || prev.physicalAddress,
            city: data.city || prev.city,
            county: data.county || prev.county,
            postalCode: data.postalCode || prev.postalCode,
            latitude: data.latitude || prev.latitude,
            longitude: data.longitude || prev.longitude,

            contactPersonName: data.contactPersonName || prev.contactPersonName,
            contactPersonTitle: data.contactPersonTitle || prev.contactPersonTitle,
            contactPersonDepartment: data.contactPersonDepartment || prev.contactPersonDepartment,
            contactPersonPhone: data.contactPersonPhone || prev.contactPersonPhone,
            contactPersonEmail: data.contactPersonEmail || prev.contactPersonEmail,

            websiteUrl: data.websiteUrl || prev.websiteUrl,
            totalStudentEnrollment: data.totalStudentEnrollment || prev.totalStudentEnrollment,
            numberOfPrograms: data.numberOfPrograms || prev.numberOfPrograms,
            accreditationBodies: data.accreditationBodies || prev.accreditationBodies,
            institutionDescription: data.institutionDescription || prev.institutionDescription,

            careerCenterEmail: data.careerCenterEmail || prev.careerCenterEmail,
            careerOfficePhone: data.careerOfficePhone || prev.careerOfficePhone,
            careerServicesContactPerson: data.careerServicesContactPerson || prev.careerServicesContactPerson,

            areasOfSpecialization: Array.isArray(data.areasOfSpecialization)
              ? data.areasOfSpecialization.join(', ')
              : (data.areasOfSpecialization || prev.areasOfSpecialization),

            linkedinProfile: data.linkedinProfile || prev.linkedinProfile,
            facebookPage: data.facebookPage || prev.facebookPage,
            twitterHandle: data.twitterHandle || prev.twitterHandle,
            languagePreference: data.languagePreference || prev.languagePreference,
            showLogoOnWebsite: (typeof data.showLogoOnWebsite === 'boolean') ? data.showLogoOnWebsite : (prev.showLogoOnWebsite ?? true),
            profilePhotoPath: data.profilePhotoPath || data.profileImage || data.profilePhoto || prev.profilePhotoPath
          }));

          // Calculate profile completion - use backend value if available, otherwise calculate locally
          let completion = 0;
          if (typeof data.profileCompletion === 'number') {
            completion = data.profileCompletion;
          } else {
            // Calculate locally based on university-specific fields (same logic as dashboard)
            const requiredFields = [
              'universityName',
              'institutionType',
              'registrationNumber',
              'officialEmail',
              'phoneNumber',
              'physicalAddress',
              'city',
              'county',
              'contactPersonName',
              'contactPersonTitle',
              'contactPersonPhone',
              'contactPersonEmail'
            ];
            
            // Count filled required fields
            const filledFields = requiredFields.filter(field => {
              const value = data[field];
              return value && value.toString().trim() !== '';
            }).length;
            
            // Check for optional but important fields
            const optionalFields = [
              'websiteUrl',
              'totalStudentEnrollment',
              'numberOfPrograms',
              'careerCenterEmail',
              'careerOfficePhone',
              'areasOfSpecialization'
            ];
            
            const filledOptional = optionalFields.filter(field => {
              const value = data[field];
              if (field === 'areasOfSpecialization') {
                return Array.isArray(value) ? value.length > 0 : (value && value.toString().trim() !== '');
              }
              return value && value.toString().trim() !== '';
            }).length;
            
            // Calculate completion: 70% for required fields, 30% for optional
            const requiredPercentage = (filledFields / requiredFields.length) * 70;
            const optionalPercentage = (filledOptional / optionalFields.length) * 30;
            completion = Math.round(requiredPercentage + optionalPercentage);
            
            // If profile is marked as completed, ensure it shows 100%
            if (data.profileCompleted || data.hasCompletedProfile) {
              completion = 100;
            }
          }
          
          setProfileCompletionPercentage(Math.max(0, Math.min(100, completion)));
        }
      } catch (error) {
        console.error('Error loading university profile:', error);
      }
    };

    loadUniversityProfile();
  }, []);

  // Recalculate profile completion when profileForm changes
  useEffect(() => {
    const calculateCompletion = () => {
      const requiredFields = [
        'universityName',
        'institutionType',
        'registrationNumber',
        'officialEmail',
        'phoneNumber',
        'physicalAddress',
        'city',
        'county',
        'contactPersonName',
        'contactPersonTitle',
        'contactPersonPhone',
        'contactPersonEmail'
      ];
      
      const filledFields = requiredFields.filter(field => {
        const value = profileForm[field];
        return value && value.toString().trim() !== '';
      }).length;
      
      const optionalFields = [
        'websiteUrl',
        'totalStudentEnrollment',
        'numberOfPrograms',
        'careerCenterEmail',
        'careerOfficePhone',
        'areasOfSpecialization'
      ];
      
      const filledOptional = optionalFields.filter(field => {
        const value = profileForm[field];
        if (field === 'areasOfSpecialization') {
          return Array.isArray(value) ? value.length > 0 : (value && value.toString().trim() !== '');
        }
        return value && value.toString().trim() !== '';
      }).length;
      
      const requiredPercentage = (filledFields / requiredFields.length) * 70;
      const optionalPercentage = (filledOptional / optionalFields.length) * 30;
      const completion = Math.round(requiredPercentage + optionalPercentage);
      
      setProfileCompletionPercentage(Math.max(0, Math.min(100, completion)));
    };

    // Only recalculate if we have some data loaded (not initial empty state)
    if (profileForm.universityName || profileForm.officialEmail) {
      calculateCompletion();
    }
  }, [profileForm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleSectionEdit = (section) => {
    setEditingSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleInstitutionLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
      if (!file.type.match(/^image\/(png|jpg|jpeg)$/i)) {
        alert('Only PNG and JPG images are allowed');
        e.target.value = '';
        return;
      }
      setInstitutionLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setInstitutionLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
      if (!file.type.match(/^image\/(png|jpg|jpeg)$/i)) {
        alert('Only PNG and JPG images are allowed');
        e.target.value = '';
        return;
      }
      setProfilePhotoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setProfilePhotoPreview(e.target.result);
      reader.readAsDataURL(file);
      
      // Auto-save the photo immediately
      try {
        setSaving(true);
        const token = localStorage.getItem('token');
        if (!token) {
          alert('Please log in to upload photo');
          return;
        }
        
        const formData = new FormData();
        formData.append('profilePhoto', file);
        
        // Include all profile data to ensure the save works
        const minimalPayload = {
          ...profileForm
        };
        
        // Append all form fields
        Object.keys(minimalPayload).forEach(key => {
          const value = minimalPayload[key];
          if (value !== null && value !== undefined) {
            if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });
        
        const response = await axios.put(
          buildApiUrl('/api/user/profile'),
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        if (response.data) {
          // Clear the photo file after successful upload
          setProfilePhotoFile(null);
          if (profilePhotoInputRef.current) {
            profilePhotoInputRef.current.value = '';
          }
          
          // Reload profile to get updated photo path
          const refreshResponse = await axios.get(
            buildApiUrl('/api/user/profile'),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (refreshResponse.data) {
            const data = refreshResponse.data;
            const photoPath = data.profileImage || data.profilePhotoPath || data.profilePhoto;
            if (photoPath) {
              let normalizedPath = photoPath.replace(/\\/g, '/');
              let fullUrl = '';
              if (normalizedPath.startsWith('uploads/')) {
                const uploadPath = normalizedPath.replace('uploads/', '');
                fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
              } else if (normalizedPath.startsWith('/uploads/')) {
                const uploadPath = normalizedPath.replace('/uploads/', '');
                fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
              } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
                fullUrl = normalizedPath;
              } else {
                fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
              }
              setProfileForm(prev => ({ ...prev, profilePhotoPath: fullUrl }));
              setProfilePhotoPreview(fullUrl);
            }
          }
          
          alert('✓ Photo uploaded successfully!');
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
        console.error('Error details:', error.response?.data);
        alert('❌ Error uploading photo. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleSave = async (section) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to save changes');
        return;
      }

      // Prepare the data to save
      const dataToSave = {
        ...profileForm
      };

      let response;
      
      // If there's a photo file or logo file, use FormData
      if (profilePhotoFile || institutionLogoFile) {
        const formData = new FormData();
        if (profilePhotoFile) {
          formData.append('profilePhoto', profilePhotoFile);
        }
        if (institutionLogoFile) {
          formData.append('institutionLogo', institutionLogoFile);
        }
        
        // Append all profile data
        Object.keys(dataToSave).forEach(key => {
          const value = dataToSave[key];
          if (value !== null && value !== undefined) {
            if (typeof value === 'object' && !Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else if (Array.isArray(value)) {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value);
            }
          }
        });

        response = await axios.put(
          buildApiUrl('/api/user/profile'),
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            } 
          }
        );
        
        // Clear logo file after successful upload and update preview
        if (institutionLogoFile && response.data) {
          const logoPath = response.data.institutionLogo;
          if (logoPath) {
            let normalizedPath = logoPath.replace(/\\/g, '/');
            let fullUrl = '';
            if (normalizedPath.startsWith('uploads/')) {
              const uploadPath = normalizedPath.replace('uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('/uploads/')) {
              const uploadPath = normalizedPath.replace('/uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
              fullUrl = normalizedPath;
            } else {
              fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
            }
            setProfileForm(prev => ({ ...prev, institutionLogo: fullUrl }));
            setInstitutionLogoPreview(fullUrl);
          }
          setInstitutionLogoFile(null);
          if (institutionLogoInputRef.current) {
            institutionLogoInputRef.current.value = '';
          }
        }
      } else {
        // Regular JSON save
        response = await axios.put(
          buildApiUrl('/api/user/profile'),
          dataToSave,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response.data) {
        // Update profile photo path if photo was uploaded
        if (profilePhotoFile && (response.data.profileImage || response.data.profilePhotoPath)) {
          const photoPath = response.data.profileImage || response.data.profilePhotoPath;
          if (photoPath) {
            let normalizedPath = photoPath.replace(/\\/g, '/');
            let fullUrl = '';
            if (normalizedPath.startsWith('uploads/')) {
              const uploadPath = normalizedPath.replace('uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('/uploads/')) {
              const uploadPath = normalizedPath.replace('/uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
              fullUrl = normalizedPath;
            } else {
              fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
            }
            setProfileForm(prev => ({ ...prev, profilePhotoPath: fullUrl }));
            setProfilePhotoPreview(fullUrl);
          }
          setProfilePhotoFile(null);
          if (profilePhotoInputRef.current) {
            profilePhotoInputRef.current.value = '';
          }
        }
        
        alert(`${section} section saved successfully!`);
        toggleSectionEdit(section);
        // Reload profile to get updated data
        const refreshResponse = await axios.get(
          buildApiUrl('/api/user/profile'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (refreshResponse.data) {
          const data = refreshResponse.data;
          setProfileForm(prev => ({
            ...prev,
            universityName: data.universityName || prev.universityName,
            institutionType: data.institutionType || prev.institutionType,
            yearEstablished: data.yearEstablished || prev.yearEstablished,
            registrationNumber: data.registrationNumber || prev.registrationNumber,
            cueRegistrationNumber: data.cueRegistrationNumber || prev.cueRegistrationNumber,
            officialEmail: data.officialEmail || data.email || prev.officialEmail,
            alternativeEmail: data.alternativeEmail || prev.alternativeEmail,
            phoneNumber: data.phoneNumber || data.phone || prev.phoneNumber,
            whatsappNumber: data.whatsappNumber || prev.whatsappNumber,
            physicalAddress: data.physicalAddress || prev.physicalAddress,
            city: data.city || prev.city,
            county: data.county || prev.county,
            postalCode: data.postalCode || prev.postalCode,
            latitude: data.latitude || prev.latitude,
            longitude: data.longitude || prev.longitude,
            contactPersonName: data.contactPersonName || prev.contactPersonName,
            contactPersonTitle: data.contactPersonTitle || prev.contactPersonTitle,
            contactPersonDepartment: data.contactPersonDepartment || prev.contactPersonDepartment,
            contactPersonPhone: data.contactPersonPhone || prev.contactPersonPhone,
            contactPersonEmail: data.contactPersonEmail || prev.contactPersonEmail,
            websiteUrl: data.websiteUrl || prev.websiteUrl,
            totalStudentEnrollment: data.totalStudentEnrollment || prev.totalStudentEnrollment,
            numberOfPrograms: data.numberOfPrograms || prev.numberOfPrograms,
            accreditationBodies: data.accreditationBodies || prev.accreditationBodies,
            institutionDescription: data.institutionDescription || prev.institutionDescription,
            careerCenterEmail: data.careerCenterEmail || prev.careerCenterEmail,
            careerOfficePhone: data.careerOfficePhone || prev.careerOfficePhone,
            careerServicesContactPerson: data.careerServicesContactPerson || prev.careerServicesContactPerson,
            areasOfSpecialization: Array.isArray(data.areasOfSpecialization)
              ? data.areasOfSpecialization.join(', ')
              : (data.areasOfSpecialization || prev.areasOfSpecialization),
            linkedinProfile: data.linkedinProfile || prev.linkedinProfile,
            facebookPage: data.facebookPage || prev.facebookPage,
            twitterHandle: data.twitterHandle || prev.twitterHandle,
            languagePreference: data.languagePreference || prev.languagePreference,
            profilePhotoPath: data.profilePhotoPath || data.profileImage || data.profilePhoto || prev.profilePhotoPath,
            institutionLogo: data.institutionLogo || prev.institutionLogo,
            showLogoOnWebsite: data.showLogoOnWebsite !== false
          }));
          
          // Load institution logo preview
          if (data.institutionLogo) {
            let normalizedPath = data.institutionLogo.replace(/\\/g, '/');
            let fullUrl = '';
            if (normalizedPath.startsWith('uploads/')) {
              const uploadPath = normalizedPath.replace('uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('/uploads/')) {
              const uploadPath = normalizedPath.replace('/uploads/', '');
              fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
            } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
              fullUrl = normalizedPath;
            } else {
              fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
            }
            setInstitutionLogoPreview(fullUrl);
          }
          
          // Recalculate profile completion after save
          const requiredFields = [
            'universityName',
            'institutionType',
            'registrationNumber',
            'officialEmail',
            'phoneNumber',
            'physicalAddress',
            'city',
            'county',
            'contactPersonName',
            'contactPersonTitle',
            'contactPersonPhone',
            'contactPersonEmail'
          ];
          
          const filledFields = requiredFields.filter(field => {
            const value = data[field];
            return value && value.toString().trim() !== '';
          }).length;
          
          const optionalFields = [
            'websiteUrl',
            'totalStudentEnrollment',
            'numberOfPrograms',
            'careerCenterEmail',
            'careerOfficePhone',
            'areasOfSpecialization'
          ];
          
          const filledOptional = optionalFields.filter(field => {
            const value = data[field];
            if (field === 'areasOfSpecialization') {
              return Array.isArray(value) ? value.length > 0 : (value && value.toString().trim() !== '');
            }
            return value && value.toString().trim() !== '';
          }).length;
          
          const requiredPercentage = (filledFields / requiredFields.length) * 70;
          const optionalPercentage = (filledOptional / optionalFields.length) * 30;
          let completion = Math.round(requiredPercentage + optionalPercentage);
          
          if (data.profileCompleted || data.hasCompletedProfile) {
            completion = 100;
          }
          
          setProfileCompletionPercentage(Math.max(0, Math.min(100, completion)));
        }
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const EditButton = ({ section }) => (
    !editingSections[section] ? (
      <button
        onClick={() => toggleSectionEdit(section)}
        style={{
          background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
          color: 'white',
          border: 'none',
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
      >
        <FontAwesomeIcon icon={faEdit} /> Edit
      </button>
    ) : (
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={() => handleSave(section)}
          style={{
            background: '#10b981',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <FontAwesomeIcon icon={faSave} /> Save
        </button>
        <button
          type="button"
          onClick={() => toggleSectionEdit(section)}
          style={{
            background: '#dc2626',
            color: '#ffffff',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
      </div>
    )
  );

  const containerStyle = {
    padding: '0 24px 32px 24px',
    maxWidth: '100%',
    width: '100%',
    margin: '0',
    minHeight: '100vh',
    boxSizing: 'border-box'
  };

  const elevatedCardStyle = {
    border: '1px solid #edf2f7',
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(17, 24, 39, 0.06)',
    background: '#fff',
    marginBottom: '20px',
    padding: '25px',
    overflow: 'hidden'
  };

  const profileHeaderStyle = {
    background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
    color: '#fff',
    padding: '16px 24px',
    borderRadius: '16px',
    marginBottom: '20px',
    position: 'relative',
    overflow: 'hidden',
    minHeight: '120px',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.25)'
  };

  const headerPatternStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.1,
    backgroundImage: `
      radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0%, transparent 50%)
    `
  };

  const gridStyle = { display: 'grid', gap: '15px' };

  return (
    <>
      <style>{`
        .university-my-profile-page .form-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
        }

        .university-my-profile-page input,
        .university-my-profile-page textarea,
        .university-my-profile-page select {
          width: 100%;
          padding: 10px 12px;
          border-radius: 8px;
          border: 2px solid #e5e7eb;
          font-size: 14px;
          transition: all 0.3s ease;
          outline: none;
        }

        .university-my-profile-page input:focus,
        .university-my-profile-page textarea:focus,
        .university-my-profile-page select:focus {
          border-color: #f97316 !important;
          box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1) !important;
        }
      `}</style>

      <div
        className="university-my-profile-page"
        style={{
          display: 'flex',
          minHeight: '100vh',
          width: '100%',
          backgroundColor: '#f8fafc'
        }}
      >
        {/* Sidebar copied from EmployerMyProfile, with university content */}
        <div
          style={{
            width: '280px',
            background: 'linear-gradient(180deg, #f97316 0%, #0d9488 100%)',
            padding: '2rem 1.5rem',
            boxShadow: '4px 0 20px rgba(249, 115, 22, 0.15)',
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100vh',
            overflowY: 'auto',
            zIndex: 1000,
            color: '#ffffff'
          }}
        >
          <div style={{ marginBottom: '2rem' }}>
            <h2
              style={{
                color: '#ffffff',
                fontSize: '1.5rem',
                fontWeight: '800',
                marginBottom: '0.5rem',
                textShadow: '0 4px 8px rgba(0,0,0,0.7)',
                letterSpacing: '0.5px'
              }}
            >
              University Profile
            </h2>
            <p
              style={{
                color: '#ffffff',
                fontSize: '0.9rem',
                margin: 0,
                fontWeight: '700',
                textShadow: '0 2px 4px rgba(0,0,0,0.6)'
              }}
            >
              Manage your institution information
            </p>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <h3
              style={{
                color: '#ffffff',
                fontSize: '1.1rem',
                fontWeight: '700',
                marginBottom: '1rem',
                textShadow: '0 3px 6px rgba(0,0,0,0.7)',
                letterSpacing: '0.3px'
              }}
            >
              Quick Navigation
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() =>
                  document.querySelector('[data-section="institution"]')?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faUniversity} /> Institution Information
              </button>
              <button
                type="button"
                onClick={() =>
                  document.querySelector('[data-section="contact"]')?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faEnvelope} /> Contact Information
              </button>
              <button
                type="button"
                onClick={() =>
                  document.querySelector('[data-section="details"]')?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faInfoCircle} /> Institution Details
              </button>
              <button
                type="button"
                onClick={() =>
                  document.querySelector('[data-section="career"]')?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faBriefcase} /> Career Services
              </button>
              <button
                type="button"
                onClick={() =>
                  document.querySelector('[data-section="specialization"]')?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faGraduationCap} /> Specializations
              </button>
              <button
                type="button"
                onClick={() =>
                  document.querySelector('[data-section="social"]')?.scrollIntoView({ behavior: 'smooth' })
                }
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  border: '2px solid rgba(255,255,255,0.3)',
                  padding: '10px 15px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textAlign: 'left',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faGlobe} /> Social & Online
              </button>
            </div>
          </div>

          {/* Profile Completion (visual only for now) */}
          <div
            style={{
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              padding: '20px',
              borderRadius: '12px',
              border: '2px solid rgba(255,255,255,0.2)'
            }}
          >
            <h4
              style={{
                color: '#ffffff',
                fontSize: '1rem',
                fontWeight: '700',
                marginBottom: '12px',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)'
              }}
            >
              Profile Completion
            </h4>
            <div
              style={{
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '10px',
                height: '10px',
                overflow: 'hidden',
                marginBottom: '10px'
              }}
            >
              <div
                style={{
                  background: 'linear-gradient(90deg, #10b981 0%, #14b8a6 100%)',
                  height: '100%',
                  width: `${profileCompletionPercentage}%`,
                  transition: 'width 0.5s ease',
                  boxShadow: '0 0 10px rgba(16, 185, 129, 0.8)'
                }}
              ></div>
            </div>
            <p
              style={{
                color: '#ffffff',
                fontSize: '14px',
                margin: '0 0 15px 0',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                fontWeight: '600'
              }}
            >
              {profileCompletionPercentage}% Complete
            </p>
            <button
              type="button"
              onClick={() => navigate('/university-registration')}
              style={{
                width: '100%',
                background: 'rgba(255, 255, 255, 0.25)',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                color: '#ffffff',
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.35)';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.3)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
              }}
            >
              <FontAwesomeIcon icon={faEdit} style={{ marginRight: '8px' }} />
              Complete Profile
            </button>
          </div>
        </div>

        {/* Main content area (mirrors EmployerMyProfile layout) */}
        <div
          style={{
            marginLeft: '280px',
            flex: 1,
            padding: '40px',
            width: 'calc(100% - 280px)',
            overflowX: 'hidden',
            overflowY: 'auto',
            minHeight: '100vh',
            boxSizing: 'border-box',
            backgroundColor: '#f8fafc'
          }}
        >
          <div style={containerStyle}>
            {/* Header / hero */}
            <div style={profileHeaderStyle}>
              <div style={headerPatternStyle}></div>

              <button
                onClick={() => navigate('/university-dashboard')}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  zIndex: 10,
                  background: 'rgba(255, 255, 255, 0.25)',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  color: '#ffffff',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to Dashboard
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '30px', position: 'relative', zIndex: 2 }}>
                <div style={{ position: 'relative' }}>
                  <div
                    style={{
                      width: '76px',
                      height: '76px',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.25)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '30px',
                      fontWeight: '700',
                      color: '#ffffff',
                      border: '3px solid rgba(255,255,255,0.4)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                      textShadow: '0 4px 8px rgba(0,0,0,0.5)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {(profilePhotoPreview || profileForm.profilePhotoPath) ? (() => {
                      let imageUrl = '';
                      if (profilePhotoPreview) {
                        imageUrl = profilePhotoPreview;
                      } else {
                        const normalizedPath = profileForm.profilePhotoPath.replace(/\\/g, '/');
                        if (normalizedPath.startsWith('uploads/')) {
                          const uploadPath = normalizedPath.replace('uploads/', '');
                          imageUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
                        } else if (normalizedPath.startsWith('/uploads/')) {
                          const uploadPath = normalizedPath.replace('/uploads/', '');
                          imageUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
                        } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
                          imageUrl = normalizedPath;
                        } else {
                          imageUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
                        }
                      }
                      return (
                        <img 
                          src={imageUrl}
                          alt={profileForm.universityName || 'University'}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '50%'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      );
                    })() : null}
                    <div style={{
                      display: (profilePhotoPreview || profileForm.profilePhotoPath) ? 'none' : 'flex',
                      width: '100%',
                      height: '100%',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FontAwesomeIcon icon={faUniversity} />
                    </div>
                  </div>
                  {/* Camera button overlay */}
                  <label
                    style={{
                      position: 'absolute',
                      bottom: '0',
                      right: '0',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f97316 0%, #0d9488 100%)',
                      border: '3px solid #ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      zIndex: 10
                    }}
                    title="Upload profile photo"
                  >
                    <FontAwesomeIcon icon={faCamera} style={{ color: '#ffffff', fontSize: '12px' }} />
                    <input
                      type="file"
                      ref={profilePhotoInputRef}
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handlePhotoChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>

                <div style={{ flex: 1 }}>
                  <h2
                    style={{
                      marginBottom: '4px',
                      fontSize: '28px',
                      fontWeight: '700',
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      color: '#ffffff'
                    }}
                  >
                    {profileForm.universityName || 'University Name'}
                  </h2>
                  <p
                    style={{
                      fontSize: '16px',
                      fontWeight: '500',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      color: '#ffffff',
                      marginTop: '2px',
                      marginBottom: 0
                    }}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} />{' '}
                    {primaryLocation || 'Location not set'} ·{' '}
                    <FontAwesomeIcon icon={faUsers} />{' '}
                    {profileForm.totalStudentEnrollment || '0'} Students
                  </p>
                </div>
              </div>
            </div>

            {/* Section 1: Institution Information */}
            <div style={elevatedCardStyle} data-section="institution">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}
                >
                  <FontAwesomeIcon icon={faUniversity} style={{ color: '#f97316', marginRight: '10px' }} />
                  Institution Information
                </h3>
                <EditButton section="institution" />
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">University / Institution Name</label>
                  <input
                    type="text"
                    name="universityName"
                    value={profileForm.universityName}
                    onChange={handleChange}
                    disabled={!editingSections.institution}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Institution Type</label>
                  <select
                    name="institutionType"
                    value={profileForm.institutionType}
                    onChange={handleChange}
                    disabled={!editingSections.institution}
                  >
                    <option value="">Select Type</option>
                    {INSTITUTION_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="form-group">
                  <label className="form-label">Registration / Accreditation Number</label>
                  <input
                    type="text"
                    name="registrationNumber"
                    value={profileForm.registrationNumber}
                    onChange={handleChange}
                    disabled={!editingSections.institution}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">CUE Registration Number</label>
                  <input
                    type="text"
                    name="cueRegistrationNumber"
                    value={profileForm.cueRegistrationNumber}
                    onChange={handleChange}
                    disabled={!editingSections.institution}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Year Established</label>
                  <input
                    type="number"
                    name="yearEstablished"
                    value={profileForm.yearEstablished}
                    onChange={handleChange}
                    disabled={!editingSections.institution}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: '1fr', marginTop: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Accreditation Bodies</label>
                  <input
                    type="text"
                    name="accreditationBodies"
                    value={profileForm.accreditationBodies}
                    onChange={handleChange}
                    disabled={!editingSections.institution}
                  />
                </div>
              <div style={{ marginTop: '20px', padding: '15px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <input
                      type="checkbox"
                      name="showLogoOnWebsite"
                      checked={profileForm.showLogoOnWebsite !== false}
                      onChange={(e) =>
                        setProfileForm(prev => ({
                          ...prev,
                          showLogoOnWebsite: e.target.checked
                        }))
                      }
                      disabled={!editingSections.institution}
                      style={{ width: '16px', height: '16px', cursor: editingSections.institution ? 'pointer' : 'default' }}
                    />
                    <span style={{ fontWeight: '500' }}>Show our university logo on the Universities page</span>
                  </label>
                  <p style={{ margin: '5px 0 0 24px', fontSize: '12px', color: '#64748b' }}>
                    When enabled, your university logo will be displayed on the public Universities page.
                  </p>
                </div>
                
                {editingSections.institution && (
                  <div className="form-group">
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginBottom: '8px', display: 'block' }}>
                      Upload University Logo
                    </label>
                    {institutionLogoPreview && (
                      <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                        <img 
                          src={institutionLogoPreview} 
                          alt="University Logo Preview" 
                          style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                        />
                      </div>
                    )}
                    <label className="file-upload-label" style={{ cursor: 'pointer', padding: '12px', border: '1px dashed #cbd5e1', borderRadius: '6px', background: '#fff', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FontAwesomeIcon icon={faImage} style={{ fontSize: '20px', color: '#f97316' }} />
                      <div style={{ flex: 1 }}>
                        <strong style={{ fontSize: '13px', display: 'block', marginBottom: '2px' }}>Choose Logo File</strong>
                        <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>PNG, JPG (max 5MB)</p>
                      </div>
                      <input 
                        type="file" 
                        ref={institutionLogoInputRef}
                        accept="image/png,image/jpeg,image/jpg" 
                        onChange={handleInstitutionLogoChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                )}
              </div>
              </div>
            </div>

            {/* Section 2: Contact Information (matches registration fields) */}
            <div style={elevatedCardStyle} data-section="contact">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}
                >
                  <FontAwesomeIcon icon={faEnvelope} style={{ color: '#f97316', marginRight: '10px' }} />
                  Contact Information
                </h3>
                <EditButton section="contact" />
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Official Email</label>
                  <input
                    type="email"
                    name="officialEmail"
                    value={profileForm.officialEmail}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Alternative Email</label>
                  <input
                    type="email"
                    name="alternativeEmail"
                    value={profileForm.alternativeEmail}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">
                    <FontAwesomeIcon icon={faPhone} /> Phone Number
                  </label>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={profileForm.phoneNumber}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp Number</label>
                  <input
                    type="text"
                    name="whatsappNumber"
                    value={profileForm.whatsappNumber}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: '1fr', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Physical Address</label>
                  <input
                    type="text"
                    name="physicalAddress"
                    value={profileForm.physicalAddress}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    name="city"
                    value={profileForm.city}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">County</label>
                  <select
                    name="county"
                    value={profileForm.county}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  >
                    <option value="">Select County</option>
                    {KENYAN_COUNTIES.map((county) => (
                      <option key={county} value={county}>
                        {county}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Postal Code</label>
                  <input
                    type="text"
                    name="postalCode"
                    value={profileForm.postalCode}
                    onChange={handleChange}
                    disabled={!editingSections.contact}
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Primary Contact Person (Placement Officer) */}
            <div style={elevatedCardStyle} data-section="person">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}
                >
                  Primary Contact Person (Placement Officer)
                </h3>
                <EditButton section="details" />
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    name="contactPersonName"
                    value={profileForm.contactPersonName}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Job Title / Position</label>
                  <input
                    type="text"
                    name="contactPersonTitle"
                    value={profileForm.contactPersonTitle}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: '1fr', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <input
                    type="text"
                    name="contactPersonDepartment"
                    value={profileForm.contactPersonDepartment}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)' }}>
                <div className="form-group">
                  <label className="form-label">Direct Phone Number</label>
                  <input
                    type="text"
                    name="contactPersonPhone"
                    value={profileForm.contactPersonPhone}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    name="contactPersonEmail"
                    value={profileForm.contactPersonEmail}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Institution Details */}
            <div style={elevatedCardStyle} data-section="details">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}
                >
                  <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#f97316', marginRight: '10px' }} />
                  Institution Details
                </h3>
                <EditButton section="details" />
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Website URL</label>
                  <input
                    type="url"
                    name="websiteUrl"
                    value={profileForm.websiteUrl}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Total Student Enrollment</label>
                  <input
                    type="number"
                    name="totalStudentEnrollment"
                    value={profileForm.totalStudentEnrollment}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Number of Programs Offered</label>
                  <input
                    type="number"
                    name="numberOfPrograms"
                    value={profileForm.numberOfPrograms}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: '1fr', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Accreditation Bodies</label>
                  <input
                    type="text"
                    name="accreditationBodies"
                    value={profileForm.accreditationBodies}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: '1fr' }}>
                <div className="form-group">
                  <label className="form-label">Brief Description / About Us</label>
                  <textarea
                    name="institutionDescription"
                    value={profileForm.institutionDescription}
                    onChange={handleChange}
                    disabled={!editingSections.details}
                    rows={4}
                  />
                </div>
              </div>
            </div>

            {/* Section 5: Career Services / Placement Office */}
            <div style={elevatedCardStyle} data-section="career">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}
                >
                  <FontAwesomeIcon icon={faBriefcase} style={{ color: '#f97316', marginRight: '10px' }} />
                  Career Services
                </h3>
                <EditButton section="career" />
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">Career Center Email</label>
                  <input
                    type="email"
                    name="careerCenterEmail"
                    value={profileForm.careerCenterEmail}
                    onChange={handleChange}
                    disabled={!editingSections.career}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Career Office Phone</label>
                  <input
                    type="text"
                    name="careerOfficePhone"
                    value={profileForm.careerOfficePhone}
                    onChange={handleChange}
                    disabled={!editingSections.career}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: '1fr' }}>
                <div className="form-group">
                  <label className="form-label">Career Services Offered</label>
                  <textarea
                    name="careerServicesContactPerson"
                    value={profileForm.careerServicesContactPerson}
                    onChange={handleChange}
                    disabled={!editingSections.career}
                    rows={3}
                    placeholder="Career services contact person / office notes"
                  />
                </div>
              </div>
            </div>

            {/* Section 6: Areas of Specialization */}
            <div style={elevatedCardStyle} data-section="specialization">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}
                >
                  <FontAwesomeIcon icon={faGraduationCap} style={{ color: '#f97316', marginRight: '10px' }} />
                  Areas of Specialization
                </h3>
                <EditButton section="specialization" />
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: '1fr' }}>
                <div className="form-group">
                  <label className="form-label">Key Faculties / Specialization Areas</label>
                  <textarea
                    name="areasOfSpecialization"
                    value={profileForm.areasOfSpecialization}
                    onChange={handleChange}
                    disabled={!editingSections.specialization}
                    rows={3}
                    placeholder="E.g., Engineering, Business, Health Sciences, ICT"
                  />
                </div>
              </div>
            </div>

            {/* Section 6: Social & Online Presence */}
            <div style={elevatedCardStyle} data-section="social">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  paddingBottom: '15px',
                  borderBottom: '2px solid #f0f0f0'
                }}
              >
                <h3
                  style={{
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#1e293b',
                    margin: 0
                  }}
                >
                  <FontAwesomeIcon icon={faGlobe} style={{ color: '#f97316', marginRight: '10px' }} />
                  Social Media & Online Presence
                </h3>
                <EditButton section="social" />
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">
                    <FontAwesomeIcon icon={faLinkedin} /> LinkedIn
                  </label>
                  <input
                    type="url"
                    name="linkedinProfile"
                    value={profileForm.linkedinProfile}
                    onChange={handleChange}
                    disabled={!editingSections.social}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    <FontAwesomeIcon icon={faFacebook} /> Facebook
                  </label>
                  <input
                    type="url"
                    name="facebookPage"
                    value={profileForm.facebookPage}
                    onChange={handleChange}
                    disabled={!editingSections.social}
                  />
                </div>
              </div>

              <div style={{ ...gridStyle, gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '15px' }}>
                <div className="form-group">
                  <label className="form-label">
                    <FontAwesomeIcon icon={faTwitter} /> Twitter / X
                  </label>
                  <input
                    type="text"
                    name="twitterHandle"
                    value={profileForm.twitterHandle}
                    onChange={handleChange}
                    disabled={!editingSections.social}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Language Preference</label>
                  <select
                    name="languagePreference"
                    value={profileForm.languagePreference}
                    onChange={handleChange}
                    disabled={!editingSections.social}
                  >
                    <option value="English">English</option>
                    <option value="Swahili">Swahili</option>
                    <option value="Both">English & Swahili</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UniversityMyProfile;


